import { useAuthContext } from "../../auth/state/AuthContext";
import { ReactComponent as RemoveVessel } from "../../shared/assets/RemoveVessel.svg";
import { ReactComponent as ShipAddVessel } from "../../shared/assets/addVessel.svg";
import { ReactComponent as ShipLock } from "../../shared/assets/lockedVessel.svg";
import { toastInfo, toastSuccess } from "../../shared/components/ToastContainer";
import { api, apiUrl } from "../../shared/services/service";
import { VesselDetails } from "../models/search.interfaces";
import { datadogRum } from "@datadog/browser-rum";
import { FC } from "react";

export const VesselsRowMenu: FC<{
  fullName: string;
  item: VesselDetails;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  disabled?: boolean;
  tooltipId?: string;
}> = ({ fullName, item, setIsLoading, disabled = false, tooltipId = "search-tooltip" }) => {
  const isCoordinator = item.OPS_COORDINATOR === fullName;
  const { sub: email } = useAuthContext().state.user?.accessToken || {};

  const addToMyVessels = async (event: React.MouseEvent<HTMLButtonElement>) => {
    datadogRum.addAction("addToMyVessel", {
      ext_user_email: email,
      vessel_code: item.VESSEL_CODE,
      vessel_name: item.VESSEL_NAME,
      imo_number: item.IMO_NUMBER,
      owner: item.OWNER,
      from_imos: item.FROM_IMOS,
      ops_coordinator: item.OPS_COORDINATOR ?? "null",
    });
    event.stopPropagation();
    try {
      setIsLoading(true);

      const result = await api.post(`${apiUrl}/userAddVessel`, undefined, {
        params: {
          vessel_code: item.VESSEL_CODE,
          vessel_name: item.VESSEL_NAME,
          imo_number: item.IMO_NUMBER,
          owner: item.OWNER,
          from_imos: item.FROM_IMOS,
          ops_coordinator: item.OPS_COORDINATOR ?? "null",
          user_name: fullName,
        },
      });

      if (result.data.success) {
        item.FROM_IMOS = "N|Y";
        item.USER_NAME = fullName;
        toastSuccess("Added to My Vessels");
      }
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromMyVessels = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      setIsLoading(true);

      const result = await api.post(`${apiUrl}/userDelVessel`, undefined, {
        params: {
          vessel_code: item.VESSEL_CODE,
          vessel_name: item.VESSEL_NAME,
          imo_number: item.IMO_NUMBER,
          user_name: fullName,
        },
      });

      if (result.data.success) {
        item.FROM_IMOS = "Y";
        // This should be toastInfo but is not implemented yet
        toastInfo("Removed from My Vessels");
      }
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  };

  if (item.FROM_IMOS === "N|Y" && item.USER_NAME === fullName) {
    return (
      <button
        type="button"
        onClick={removeFromMyVessels}
        disabled={disabled}
        aria-label="Remove from My Vessels"
        title="Remove from My Vessels"
        data-test={`remove-my-vessel-${item.IMO_NUMBER}-button`}
        className="rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-green disabled:cursor-wait disabled:opacity-50"
      >
        <RemoveVessel data-tooltip-id={tooltipId} data-tooltip-html="Remove from My vessels" className="h-6 w-6" aria-hidden="true" />
      </button>
    );
  } else if ((item.FROM_IMOS === "Y" && !isCoordinator) || (item.FROM_IMOS === "N" && isCoordinator)) {
    return (
      <button
        type="button"
        onClick={addToMyVessels}
        disabled={disabled}
        aria-label="Add to My Vessels"
        title="Add to My Vessels"
        data-test={`add-my-vessel-${item.IMO_NUMBER}-button`}
        className="rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-green disabled:cursor-wait disabled:opacity-50"
      >
        <ShipAddVessel
          style={{
            cursor: "pointer",
          }}
          data-tooltip-id={tooltipId}
          data-tooltip-html="Add to My Vessels"
          className="size-6 no-underline outline-none"
          aria-hidden="true"
        />
      </button>
    );
  } else {
    return (
      <span role="img" aria-label="Assigned on IMOS, cannot be removed" title="Assigned on IMOS can’t be removed">
        <ShipLock
          className="size-6 no-underline outline-none"
          data-tooltip-id={tooltipId}
          data-tooltip-html="Assigned on IMOS can’t be removed"
          aria-hidden="true"
        />
      </span>
    );
  }
};
